import { Signal } from '@angular/core';
import { Route, UrlTree } from '@angular/router';

/**
 * One kind of item a detail drawer shows — a Dokument, an Aufgabe, an Anforderung.
 *
 * An app describes each of its kinds once and hands the table to {@link buildDetailDrawerRoute},
 * so the shell never has to know which features exist.
 *
 * @template TTypeKey - The app's names for its kinds
 */
export interface DetailDrawerType<TTypeKey extends string> {
  /** The localized segment an item of this kind stands on when another item leads to it inside the drawer. */
  readonly segment: string;
  /** The kind's name: «Datei», «Aufgabe». The crumb of the item on show, and of one above it that has not named itself. */
  readonly label: string;
  /** The dialog's accessible name while an item of this kind is on show. */
  readonly drawerLabel: string;
  /** The component that renders the item inside the shell. */
  readonly loadComponent: NonNullable<Route['loadComponent']>;
  /** Guards the item, whether it is the drawer's first level or was opened from another item. */
  readonly canActivate?: Route['canActivate'];
  /** Route data the guards read, such as `requiredPermission`. */
  readonly data?: Route['data'];
  /**
   * The kinds of item that belong to this one — an Anforderung's Aufgaben. Opened from here, one
   * stands a level below it, so the trail reads «Anforderung › Aufgabe» and leads back up.
   */
  readonly contains?: readonly TTypeKey[];
  /**
   * The kinds this one only links to — an Aufgabe's Anforderung. Following such a link shows the
   * item as the drawer's first level, with a trail of its own; the browser's back button returns.
   */
  readonly opens?: readonly TTypeKey[];
  /**
   * The kinds this one shows in a drawer of their own, stacked over it — an Anforderung's
   * Dokumente, which are looked at rather than worked on. The item stays mounted beneath, so
   * closing the stacked drawer returns to it as it was left; its page places
   * `lp-detail-drawer-stack` where that drawer mounts. A stacked item contains and stacks nothing
   * of its own: one drawer on top is the most.
   */
  readonly stacks?: readonly TTypeKey[];
}

/** Every kind of item an app's detail drawers show, by name. */
export type DetailDrawerTypes<TTypeKey extends string> = Readonly<Record<TTypeKey, DetailDrawerType<TTypeKey>>>;

/** What one level of the drawer's path carries in its route data, for the shell to read. */
export interface DetailDrawerLevel {
  readonly type: string;
  readonly label: string;
  readonly drawerLabel: string;
  /** Whether the level stands in a drawer of its own, stacked over the level above it. */
  readonly isStacked: boolean;
}

/** The route data key a level is stored under. */
export const DETAIL_DRAWER_LEVEL_DATA_KEY = 'detailDrawerLevel';

/** The matrix parameter a detail page's tab is stored under, on the segment of its own level. */
export const DETAIL_DRAWER_TAB_PARAMETER = 'tab';

/**
 * The matrix parameter naming a place within a detail page's tab, on the segment of its own level:
 * the checklist question an Aufgabe links back to. What it names is the page's to decide.
 */
export const DETAIL_DRAWER_ANCHOR_PARAMETER = 'anchor';

/** Where inside an item {@link DetailDrawerFrame.itemUrl} leads; the item's default tab where absent. */
export interface DetailDrawerItemTarget {
  /** The tab the item opens on. */
  readonly tab?: string;
  /** A place within that tab, in a form the item's page reads. */
  readonly anchor?: string;
}

/** One item on the way to another, for {@link DetailDrawerFrame.itemPathUrl}. */
export interface DetailDrawerPathStep {
  /** The item's kind, which the level before it contains. */
  readonly type: string;
  readonly id: number | string;
  /** The tab the item opens on, which its crumb returns to; its default tab where absent. */
  readonly tab?: string;
}

/** A crumb a page adds above its own for a hierarchy the route does not hold. */
export interface DetailDrawerPageCrumb {
  readonly label: string;
  readonly url: UrlTree;
}

/**
 * A page's own hierarchy within its level, which replaces the level's single crumb: an
 * Unteraufgabe stands on the same level as its Aufgabe and still reads «Aufgabe › Unteraufgabe».
 */
export interface DetailDrawerPageTrail {
  /** The crumbs above the page's own, outermost first. */
  readonly ancestors: readonly DetailDrawerPageCrumb[];
  /** The page's own crumb. */
  readonly currentLabel: string;
}

/**
 * What a page implements to be asked before it is left with unsaved changes — by another item,
 * by a crumb, by the browser's back button, and by ✕, Esc and the backdrop.
 */
export interface DetailDrawerPage {
  readonly hasUnsavedChanges: Signal<boolean>;
  /**
   * Asks whether the unsaved changes may be dropped, and drops them when they may, so the
   * navigation that follows is not asked about again.
   *
   * @returns {Promise<boolean>} Whether the page may be left
   */
  confirmLeave(): Promise<boolean>;
}

/**
 * @param {unknown} component - The component a route renders
 * @returns {boolean} Whether it implements {@link DetailDrawerPage}
 */
export function isDetailDrawerPage(component: unknown): component is DetailDrawerPage {
  const candidate = component as Partial<DetailDrawerPage> | null;
  return typeof candidate?.hasUnsavedChanges === 'function' && typeof candidate.confirmLeave === 'function';
}
